import { memo, useState, useMemo } from 'react';
import { RATING_COLORS } from '../../../utils/trafficLight';
import { useOverviewData } from './useOverviewData';
import { useApp } from '../../../context/AppContext';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { AlertTriangle, Info, Eye } from 'lucide-react';
import { UnmatchedResultsModal } from '../../UnmatchedResultsModal';
import { MultiAowResultsModal } from '../../MultiAowResultsModal';

export const ProgressAgainstTargetsTable = memo(function ProgressAgainstTargetsTable({ areasOfWork: areasOfWorkProp, aowFilter }) {
  const { areasOfWork: areasOfWorkHook } = useOverviewData();
  const { trafficLights, filteredResults } = useApp();
  const areasOfWorkRaw = areasOfWorkProp ?? areasOfWorkHook;
  const areasOfWork = useMemo(
    () => aowFilter ? (areasOfWorkRaw || []).filter(a => a.id === aowFilter) : areasOfWorkRaw,
    [areasOfWorkRaw, aowFilter]
  );
  const [showUnmatchedModal, setShowUnmatchedModal] = useState(false);
  const [showMultiAowModal, setShowMultiAowModal] = useState(false);

  const multiAowData = useMemo(() => {
    const computed = trafficLights.computed;
    if (!computed?.length) return null;

    const resultToAows = new Map();
    for (const aow of computed) {
      if (!aow.aowResultIds) continue;
      for (const rid of aow.aowResultIds) {
        if (!resultToAows.has(rid)) resultToAows.set(rid, new Set());
        resultToAows.get(rid).add(aow.name);
      }
    }

    // Build lookup from filteredResults for enrichment
    const resultLookup = new Map();
    for (const r of filteredResults) {
      const rid = r.resultCode || `${r.title}::${r.type}::${r.initiative}`;
      resultLookup.set(rid, r);
    }

    const multiAowResults = [];
    for (const [rid, aows] of resultToAows) {
      if (aows.size >= 2) {
        multiAowResults.push({ resultId: rid, aows: [...aows], result: resultLookup.get(rid) });
      }
    }

    if (multiAowResults.length === 0) return null;
    return { count: multiAowResults.length, results: multiAowResults.filter(r => r.result) };
  }, [trafficLights.computed, filteredResults]);

  const unmatchedData = useMemo(() => {
    const meta = trafficLights.unmatchedMeta;
    if (!meta) return null;

    const totalSidebar = meta.totalSidebar;
    const totalInTable = meta.totalMatchedToAow;
    const allUnmatchedRaw = [...meta.unmatchedInPool, ...meta.notInPool];
    if (allUnmatchedRaw.length === 0) return { totalSidebar, totalInTable, diff: 0, allUnmatched: [] };

    // Build lookup from filteredResults for enrichment
    const resultLookup = new Map();
    for (const r of filteredResults) {
      const rid = r.resultCode || `${r.title}::${r.type}::${r.initiative}`;
      resultLookup.set(rid, r);
    }

    const allUnmatched = allUnmatchedRaw
      .map(u => ({ ...u, result: resultLookup.get(u.resultId) }))
      .filter(u => u.result);

    return { totalSidebar, totalInTable, diff: allUnmatched.length, allUnmatched };
  }, [trafficLights.unmatchedMeta, filteredResults]);

  if (trafficLights.tocWarning) {
    return (
      <ChartWrapper title="Progress Against Targets" info="Summary of progress for each Area of Work against its Theory of Change targets. Traffic light colors: green (≥80% achieved), yellow (50–79%), red (<50%)." chartData={[]} exportFilename="progress-against-targets">
        <div className="p-5 flex gap-3 bg-red-50 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-red-800">Missing Theory of Change Data</h4>
            <p className="text-sm text-red-700 mt-1">{trafficLights.tocWarning}</p>
          </div>
        </div>
      </ChartWrapper>
    );
  }

  if (!areasOfWork || areasOfWork.length === 0) return (
    <ChartWrapper title="Progress Against Targets">
      <NoChartData message="No Areas of Work found in ToC data" detail="This table shows progress-against-targets by Area of Work using Theory of Change indicator data. No AoW definitions were found in the ToC data for this program." />
    </ChartWrapper>
  );

  const flattenIndicators = (a) => {
    const hlos = a.hlos?.length ? a.hlos : (a.indicators ? [{ indicators: a.indicators }] : []);
    return hlos.flatMap(h => (h.indicators || []).filter(i => !i.isUnaligned)).filter(i => i.achievementPct !== null);
  };

  // Prepare chart data for download
  const chartData = useMemo(() => {
    return [...areasOfWork].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })).map(aow => {
      const wt = flattenIndicators(aow);
      return {
        area_of_work: aow.name,
        rating: aow.rating,
        indicators_with_target: wt.length,
        indicators_met_target: wt.filter(i => i.achievementPct >= 100).length,
        achievement_percent: aow.achievementPct != null ? Math.round(aow.achievementPct) : aow.progress || 0,
        result_count: aow.resultCount || 0,
      };
    });
  }, [areasOfWork]);

  const totalIndicators = areasOfWork.reduce((s, a) => s + flattenIndicators(a).length, 0);
  const totalAchieved = areasOfWork.reduce((s, a) => s + flattenIndicators(a).filter(i => i.achievementPct >= 100).length, 0);
  const totalResults = areasOfWork.reduce((s, a) => s + (a.resultCount || 0), 0);

  return (
    <ChartWrapper title="Progress Against Targets" info="Summary of progress for each Area of Work against its Theory of Change targets. Traffic light colors: green (≥80% achieved), yellow (50–79%), red (<50%)." chartData={chartData} exportFilename="progress-against-targets">
      <div className="px-5 py-3 border-b border-gray-200 bg-gray-50" style={{display: 'none'}}>
        <h4 className="font-medium text-gray-900">Progress Against Targets</h4>
      </div>
      <div className="flex gap-2.5 px-4 py-2.5 mx-4 mt-3 mb-2.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
        <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="text-sm font-semibold mb-0.5">How achievement is calculated</h4>
          <p>
            Achievement percentages reflect the average completion rate across all indicators
            within each Area of Work (each capped at 100%), not the proportion of indicators
            that have fully met their annual targets.
          </p>
        </div>
      </div>
      {(unmatchedData?.diff > 0 || multiAowData) && (
        <div className="flex gap-2.5 px-4 py-2.5 mx-4 mt-1 mb-5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
          <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold mb-1">How results are counted</h4>
            <ul className="space-y-1.5 list-disc pl-4">
              <li>
                This program has <strong>{unmatchedData?.totalSidebar ?? totalResults}</strong> total results.
              </li>
              {unmatchedData?.diff > 0 && (
                <li>
                  <div className="flex items-start gap-2">
                    <span className="flex-1">
                      Of those, <strong>{unmatchedData.diff}</strong>{' '}
                      were not mapped to any Area of Work (no ToC mapping, unmatched mappings, or unconfirmed contributions),
                      leaving <strong>{unmatchedData.totalInTable}</strong> unique results in this table.
                    </span>
                    <button
                      onClick={() => setShowUnmatchedModal(true)}
                      className="flex items-center gap-1 px-2.5 py-1 text-blue-800 bg-blue-200 hover:bg-blue-300 border border-blue-400 rounded-md transition-colors whitespace-nowrap flex-shrink-0 font-medium"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      Preview unmapped
                    </button>
                  </div>
                </li>
              )}
              {multiAowData && (
                <li>
                  <div className="flex items-start gap-2">
                    <span className="flex-1">
                      {unmatchedData?.diff > 0
                        ? <>Of those, <strong>{multiAowData.count}</strong>{' '}</>
                        : <><strong>{multiAowData.count}</strong> of those{' '}</>
                      }
                      are mapped to more than a single AoW, so the per-AoW totals below sum
                      to <strong>{totalResults}</strong>.
                    </span>
                    <button
                      onClick={() => setShowMultiAowModal(true)}
                      className="flex items-center gap-1 px-2.5 py-1 text-blue-800 bg-blue-200 hover:bg-blue-300 border border-blue-400 rounded-md transition-colors whitespace-nowrap flex-shrink-0 font-medium"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      Preview multi-AoW
                    </button>
                  </div>
                </li>
              )}
            </ul>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-700">Area of Work</th>
              <th className="text-center px-3 py-3 font-medium text-gray-700">Rating</th>
              <th className="text-right px-3 py-3 font-medium text-gray-700">Indicators</th>
              <th className="text-right px-3 py-3 font-medium text-gray-700">Met Annual Target</th>
              <th className="text-right px-3 py-3 font-medium text-gray-700">Achievement</th>
              <th className="text-right px-4 py-3 font-medium text-gray-700">Total Results</th>
            </tr>
          </thead>
          <tbody>
            {[...areasOfWork].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })).map((aow, idx) => {
              const colors = RATING_COLORS[aow.ratingKey] || RATING_COLORS.unknown;
              const withTarget = flattenIndicators(aow);
              const itemCount = withTarget.length;
              const metTarget = withTarget.filter(i => i.achievementPct >= 100).length;
              return (
                <tr key={idx} className={`border-t border-gray-100 ${colors.rowBg}`}>
                  <td className="px-4 py-3 font-medium text-gray-900">{aow.name}</td>
                  <td className="px-3 py-3 text-center">
                    <span className="inline-flex items-center gap-1.5">
                      <span className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} />
                      <span className={`text-xs font-medium ${colors.text}`}>{aow.rating}</span>
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right text-gray-600">{itemCount}</td>
                  <td className="px-3 py-3 text-right text-gray-600">{metTarget}</td>
                  <td className="px-3 py-3 text-right font-medium text-gray-700">
                    {aow.achievementPct != null ? `${Math.round(aow.achievementPct)}%` : `${aow.progress || 0}%`}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-600">{aow.resultCount || 0}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-gray-50 border-t-2 border-gray-300">
            <tr>
              <td className="px-4 py-3 font-semibold text-gray-900">Total</td>
              <td className="px-3 py-3" />
              <td className="px-3 py-3 text-right font-semibold text-gray-700">{totalIndicators}</td>
              <td className="px-3 py-3 text-right font-semibold text-gray-700">{totalAchieved}</td>
              <td className="px-3 py-3 text-right font-semibold text-gray-700">
                {totalIndicators > 0 ? `${Math.round((totalAchieved / totalIndicators) * 100)}%` : '—'}
              </td>
              <td className="px-4 py-3 text-right font-semibold text-gray-700">{totalResults}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {showUnmatchedModal && unmatchedData && (
        <UnmatchedResultsModal
          results={unmatchedData.allUnmatched}
          totalSidebar={unmatchedData.totalSidebar}
          totalInTable={unmatchedData.totalInTable}
          onClose={() => setShowUnmatchedModal(false)}
        />
      )}
      {showMultiAowModal && multiAowData && (
        <MultiAowResultsModal
          results={multiAowData.results}
          onClose={() => setShowMultiAowModal(false)}
        />
      )}
    </ChartWrapper>
  );
});
